import './InputTokenizer.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createEditor, Transforms, Text, Editor, NodeEntry, Descendant } from 'slate';
import { Slate, Editable, withReact, RenderLeafProps, ReactEditor } from 'slate-react';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Tooltip } from 'antd';
import { EnvVariable } from '../../models/Environment';
import Avatar from 'antd/es/avatar/Avatar';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';
import AppLoader from '../common/AppLoader';

interface InputTokenizerProps {
  variables?: EnvVariable[];
  placeholder?: string;
  value?: string;
  className?: string;
  autoFocus?: boolean;
  onChange?: (value: string) => void;
  onVariableSelect?: (variable: EnvVariable) => void;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
}

type CustomText = {
  text: string;
  token?: boolean;
  tokenDelimiter?: boolean;
  delimiterMode?: 'start' | 'end';
  invalid?: boolean;
  details?: EnvVariable;
};

type CustomElement = {
  type: 'paragraph';
  children: CustomText[];
};

declare module 'slate' {
  interface CustomTypes {
    // Editor: Editor;
    Element: CustomElement;
    Text: CustomText;
  }
}

type Suggestion = EnvVariable & {
  noMatch?: boolean;
}

const InputTokenizer: React.FC<InputTokenizerProps> = ({ variables, placeholder, value = "", className, autoFocus, onChange, onVariableSelect, onBlur }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const editor = useMemo(() => withReact(createEditor()), []);
  const [suggestions, setSuggestions] = useState<Suggestion[]>();
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const variablesNames = useMemo(() => variables?.map(v => v.name), [variables]);

  const envVarLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "environment-variables"));

  const slateValue = useMemo((): Descendant[] => {
    return [{
      type: 'paragraph',
      children: [{ text: typeof value === 'string' ? value : `${value}` }],
    }];
  }, [value]);

  useEffect(() => {
    if (showSuggestions) setSuggestionIndex(0);
  }, [showSuggestions]);

  useEffect(() => {
    if (autoFocus) {
      ReactEditor.focus(editor);
      Transforms.select(editor, Editor.end(editor, []));
      updateSuggestions(Editor.string(editor, []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const menu = containerRef.current?.querySelector('.suggestions-menu');
    const activeItem = menu?.querySelector('.suggestions-menu-item.selected') as HTMLDivElement;

    if (activeItem && menu) {
      const itemTop = activeItem.offsetTop;
      const itemBottom = itemTop + activeItem.offsetHeight;

      if (itemTop < menu.scrollTop) {
        menu.scrollTop = itemTop;
      } else if (itemBottom > menu.scrollTop + menu.clientHeight) {
        menu.scrollTop = itemBottom - menu.clientHeight;
      }
    }
  }, [suggestionIndex]);

  const decorate = useCallback(([node, path]: NodeEntry) => {
    const ranges: any[] = [];

    if (!Text.isText(node)) return ranges;

    const regex = /\{\{(.*?)\}\}/g;
    const text = node.text;
    let match;

    while ((match = regex.exec(text))) {
      const start = match.index;
      const token = match[1];
      const invalid = !variablesNames?.includes(token);

      const openStart = start;
      const openEnd = start + 2;

      const tokenStart = openEnd;
      const tokenEnd = openEnd + token.length;

      const closeStart = tokenEnd;
      const closeEnd = closeStart + 2;

      // `{{`
      ranges.push({
        anchor: { path, offset: openStart },
        focus: { path, offset: openEnd },
        tokenDelimiter: true,
        delimiterMode: 'start',
        invalid,
      });

      // `variableName`
      ranges.push({
        anchor: { path, offset: tokenStart },
        focus: { path, offset: tokenEnd },
        token: true,
        invalid,
        details: variables?.find(v => v.name === token),
      });

      // `}}`
      ranges.push({
        anchor: { path, offset: closeStart },
        focus: { path, offset: closeEnd },
        tokenDelimiter: true,
        delimiterMode: 'end',
        invalid,
      });
    }

    return ranges;
  }, [variablesNames, variables]);

  const updateSuggestions = useCallback((text: string) => {
    const match = text.match(/\{\{(\w*)$/);
    if (match) {
      const query = match[1];
      const filtered: Suggestion[] = variables?.filter(v => v.name.startsWith(query)) || [];
      if (filtered.length === 0) filtered.push({ name: query, type: 'string', value: '', noMatch: true });
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  }, [variables]);

  const handleChange = useCallback((newValue: Descendant[]) => {
    const text = Editor.string(editor, []);
    onChange?.(text);
    updateSuggestions(text);
  }, [editor, onChange, updateSuggestions]);

  const insertSuggestion = useCallback((suggestion: Suggestion) => {
    const { selection } = editor;
    if (!selection) return;

    const fullText = Editor.string(editor, []);
    const caretOffset = selection.anchor.offset;

    // Cerca token esistente prima del caret
    const tokenStart = fullText.lastIndexOf('{{', caretOffset);
    const tokenEnd = fullText.indexOf('}}', caretOffset);

    const isInsideToken =
      tokenStart !== -1 &&
      tokenEnd !== -1 &&
      tokenStart < caretOffset &&
      caretOffset <= tokenEnd + 2;

    if (isInsideToken) {
      // Sostituisce l'intero token esistente
      const range = {
        anchor: { path: selection.anchor.path, offset: tokenStart },
        focus: { path: selection.anchor.path, offset: tokenEnd + 2 },
      };

      Transforms.select(editor, range);
      Transforms.delete(editor);
      Transforms.insertText(editor, `{{${suggestion.name}}}`);
    } else if (tokenStart !== -1 && !fullText.slice(tokenStart, caretOffset).includes('}}')) {
      // Token ancora in digitazione ("{{quer"): sostituisce da `{{` fino al caret
      // invece di appendere il nome dopo il testo gia' scritto
      const range = {
        anchor: { path: selection.anchor.path, offset: tokenStart },
        focus: { path: selection.anchor.path, offset: caretOffset },
      };

      Transforms.select(editor, range);
      Transforms.delete(editor);
      Transforms.insertText(editor, `{{${suggestion.name}}}`);
    } else {
      // Inserisce un nuovo token nel punto corrente
      if (suggestion.noMatch) Transforms.insertText(editor, '}}');
      else Transforms.insertText(editor, tokenStart > -1 ? `${suggestion.name}}}` : `{{${suggestion.name}}}`);
    }
    // 🔥 Mantieni il focus sull'editor
    ReactEditor.focus(editor);
    setShowSuggestions(false);

    if (!suggestion.noMatch) onVariableSelect?.(suggestion);
  }, [editor, onVariableSelect]);

  const handleTokenClick = useCallback(() => {
    setShowSuggestions(true);
    setSuggestions(variables);
  }, [variables]);

  const handleKeyDown = useCallback((evt: React.KeyboardEvent<HTMLDivElement>) => {
    if (showSuggestions && suggestions) {
      if (evt.key === 'ArrowDown') {
        evt.preventDefault();
        setSuggestionIndex(i => (i + 1) % suggestions.length);
      } else if (evt.key === 'ArrowUp') {
        evt.preventDefault();
        setSuggestionIndex(i => (i - 1 + suggestions.length) % suggestions.length);
      } else if (evt.key === 'Enter') {
        evt.preventDefault();
        insertSuggestion(suggestions[suggestionIndex]);
      } else if (evt.key === 'Escape') {
        setShowSuggestions(false);
      }
    }
    else {
      if (evt.key === 'Backspace') {
        const { selection } = editor;
        if (!selection) return;

        const point = selection.anchor;
        const offset = point.offset;
        const text = Editor.string(editor, []);

        // Controlla se il caret è subito dopo un token "{{...}}"
        const before = text.slice(0, offset);
        const tokenMatch = before.match(/\{\{[^}]+}}$/);

        if (tokenMatch) {
          const tokenStart = offset - tokenMatch[0].length;
          const range = {
            anchor: { path: point.path, offset: tokenStart },
            focus: { path: point.path, offset },
          };

          Transforms.select(editor, range);
          Transforms.delete(editor);
          evt.preventDefault(); // Blocca il comportamento di default
        }
      }
    }
  }, [insertSuggestion, showSuggestions, suggestionIndex, suggestions, editor]);

  const handleInsertSuggestion = useCallback((suggestion: Suggestion) => () => insertSuggestion(suggestion), [insertSuggestion]);

  const renderLeaf = useCallback((props: RenderLeafProps) => {
    const { attributes, children, leaf } = props;

    if (leaf.tokenDelimiter) return <div {...attributes} className={classNames('token-delimiter', leaf.delimiterMode)}>{children}</div>

    if (leaf.token) {
      return (
        <div
          {...attributes}
          onClick={handleTokenClick}
          className={classNames('token', { invalid: leaf.invalid })}
        >
          {leaf.invalid && (
            <Tooltip title={`This ${envVarLabels.singular} does not exist`} placement="left">
              <FontAwesomeIcon size="xs" className="mx-1 align-middle" icon={solid('exclamation-triangle')} />
            </Tooltip>
          )}
          {leaf.details?.img && (
            <Avatar
              src={leaf.details?.img}
              icon={<FontAwesomeIcon icon={solid('image')} size="xl" />}
              className="border mx-1"
              style={{ height: 20, width: 20 }}
            />
          )}
          <span className="token-name">{children}</span>
        </div>
      )
    }

    return <span {...attributes}>{children}</span>;
  }, [handleTokenClick, envVarLabels]);

  return (
    <div className={classNames("input-tokenizer", className)} ref={containerRef}>
      <Slate editor={editor} initialValue={slateValue} onChange={handleChange}>
        <Editable
          decorate={decorate}
          renderLeaf={renderLeaf}
          placeholder={placeholder || 'Type something here...'}
          className="input-box"
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
        />
      </Slate>
      {showSuggestions && (
        <ul className="suggestions-menu shadow-sm">
          {suggestions
            ? suggestions.map((suggestion, i) => (
              <li
                key={suggestion.id || i}
                onClick={handleInsertSuggestion(suggestion)}
                className={classNames("suggestions-menu-item", { selected: suggestionIndex === i })}
              >
                <Avatar
                  src={suggestion.img}
                  icon={<FontAwesomeIcon icon={suggestion.noMatch ? solid('plus') : solid('image')} size="xs" />}
                  className="border"
                  style={{ height: 25, width: 25 }}
                />
                <span className="ms-2">{suggestion.name}</span>
              </li>
            ))
            : <AppLoader className="py-3" justify="center" size={50} />
          }
        </ul>
      )}
    </div>
  );
};

export default InputTokenizer;
